import React, {useEffect, useState} from 'react';
import {useHistory} from 'react-router-dom';

import {FormState, initialFieldState, initialFormState} from '../components/Form';
import {Flow, FlowFetchResponse} from '../_services/Flow';
import {calculationFieldValues, fieldValue} from '../_helpers/form';
import {validate} from '../_helpers/form';
import {Field, StatusMessage} from '../types';

/**
 * Interface for the return value of useEntry custom hook.
 * @interface UseEntryReturn
 *
 * @property {object} entry
 * An object defining the entry of the form.
 *
 * @property {FormState} formState
 * FormState interface defining the state of a form.
 *
 * @property {(formState: FormState | ((prevState: FormState) => FormState)) => void} setFormState
 * A function that sets a new form state. This can be a newState value or a function that manipulates the previous state and returns a newState.
 *
 * @property {() => void} refreshEntry
 * A function that refreshes the current entry.
 *
 * @property {boolean} entryIsLoading
 * A boolean indicating if the form entry is loading.
 *
 * @property {string} entryLoadErrorMessage
 * A string that holds any entry load error message.
 *
 * @property {(status: string, requireValidation: boolean, data?: any) => (event: any) => void} processStatusUpdate
 * A function that processes the status update.
 *
 * @property {boolean} isDirty
 * A boolean indicating if the state has been modified since the last commit.
 *
 * @property {string} statusIsUpdating
 * A string that indicates the current status of the form entry update.
 *
 * @property {string} statusUpdateErrorMessage
 * A string that holds any error message generated during a status update.
 *
 * @property {string} entryNote
 * A string to hold entry notes.
 *
 * @property {(note: string | ((prevState: string) => string)) => void} setEntryNote
 * A function that sets a new entry note. This can be a newNote value or a function that manipulates the previous note and returns a newNote.
 *
 * @property {string} entryNoteErrorMessage
 * A string to hold entry note error message.
 *
 * @property {() => void} clearEntryNoteError
 * A function to clear entry note error message.
 *
 * @property {(action: string, data?: any) => void} doAdminAction
 * A function to perform admin actions to the form entry.
 *
 * @property {(stepId: string|number) => void} doUserChangeStep
 * A function to perform a user's step change action.
 *
 * @property {string} userChangeStepErrorMessage
 * A string to hold error message of userChangeStep.
 */
interface UseEntryReturn {
    entry: object;
    formState: FormState;
    setFormState: (formState: FormState | ((prevState: FormState) => FormState)) => void;
    refreshEntry: () => void;
    entryIsLoading: boolean;
    entryLoadErrorMessage: string;
    processStatusUpdate: (status: string, requireValidation: boolean, data?: any) => (event: any) => void;
    isDirty: boolean
    statusIsUpdating: string;
    statusUpdateErrorMessage: string;
    entryNote: string;
    setEntryNote: (note: string | ((prevState: string) => string)) => void;
    entryNoteErrorMessage: string;
    clearEntryNoteError: () => void;
    doAdminAction: (action: string, data?: any) => void;
    doUserChangeStep: (stepId: string|number) => void;
    userChangeStepErrorMessage: string;
};

/**
 * Represents a reusable hook to manage workflow entry details and status updates.
 *
 * NOTE: This hook assume your app is using react-router and if after updating an entry, the step is configured
 * to route the user (i.e. /inbox) this is handle by the hook
 *
 * @param {string} entryId - The id of the entry to be managed.
 * @param {(message: StatusMessage) => void} dispatchStatusMessage - Callback function to dispatch status messages.
 * @returns {UseEntryReturn} The return value containing entry details and functions for managing entry state.
 */
export const useEntry = (
    entryId: string,
    dispatchStatusMessage: (message: StatusMessage) => void
): UseEntryReturn => {
    const history = useHistory();

    const [formState, setFormState] = useState<FormState>({
        ...initialFormState,
        fieldValues: {}
    });
    const [entry, setEntry]: any = useState({});
    const [entryIsLoading, setEntryIsLoading]: any = useState(false);
    const [entryLoadErrorMessage, setEntryLoadErrorMessage]: any = useState('');

    // this is used to trigger the useEffect hook to update the entry
    // not a great approach and needs to be updated
    const [entryRefreshCounter, setEntryRefreshCounter] = useState<number>(1);

    const [isDirty, setIsDirty] = useState<boolean>(false);
    const isDirtyRef = React.useRef(isDirty);
    const [fieldStateUpdateCount, setFieldStateUpdateCount] = useState<number>(0);

    const [statusIsUpdating, setStatusIsUpdating] = useState<string>('');
    const [statusUpdateErrorMessage, setStatusUpdateErrorMessage] = useState<string>('');

    const [entryNote, setEntryNote] = useState<string>('');
    const [entryNoteErrorMessage, setEntryNoteErrorMessage] = useState<string>('');
    const [userChangeStepErrorMessage, setUserChangeStepErrorMessage] = useState<string>('');

    useEffect(() => {
        setEntryIsLoading(true);
        setEntryLoadErrorMessage('');

        // todo - implement abort - https://robwise.github.io/blog/cancel-whatwg-fetch-requests-in-react
        Flow.getInstance().getEntry(entryId)
            .then((response: FlowFetchResponse) => {
                if (response.ok && response.json.entry) {

                    // JDM - temp hack until this can become a configuration option
                    if (response.json.entry.form_id === '266') {
                        window.location.href = '/p/adr/view-request/' + entryId;
                        return;
                    }

                    // define form state
                    const state = {
                        ...formState,
                        fieldState: {...initialFieldState},
                        fieldValues: {
                            ...response.json.entry,
                        }
                    };

                    // process any calculation fields
                    const calculatedFields = calculationFieldValues(response.json.form, state, response.json.form.calculationFields);

                    // set entry and formState
                    setEntry(response.json);
                    setFormState({
                        ...state,
                        fieldValues: {
                            ...state.fieldValues,
                            ...calculatedFields
                        }
                    });
                } else {
                    throw Error(response?.json?.message);
                }
            })
            .catch((error: any) => {
                setEntryLoadErrorMessage(error?.message || 'Exception loading entry');
            })
            .finally(() => {
                setEntryIsLoading(false);
            })
    }, [entryId, entryRefreshCounter]);

    useEffect(() => {
        setFieldStateUpdateCount((n: number) => n + 1);

        // skip the first couple of state updates,
        // this is far from ideal, but good enough for now
        if (fieldStateUpdateCount > 1) {
            setIsDirty(true);
            isDirtyRef.current = true;
        }
    }, [formState.fieldValues]);

    /**
     *
     * @param status new workflow status
     * @param requireValidation whether the form should be validated before performing status update
     * @param data any associated data with the request
     */
    const processStatusUpdate = (status: string, requireValidation: boolean, data: any = {}) => () => {
        setEntryNoteErrorMessage('');
        setStatusUpdateErrorMessage('');

        console.log('processStatusUpdate called with', status, requireValidation, data);

        setIsDirty(false);
        isDirtyRef.current = false;

        // NOTE - not ideal, needs to be rewritten for validation logic to be a part of configuration
        // tslint:disable-next-line:triple-equals
        if (entry.form.id == '232' && status === 'rejected') {
            const rejectReason = fieldValue(formState, entry.form.fieldMap['79']);
            if (!rejectReason || rejectReason?.length < 1) {
                alert('You must provide Reason for Rejection');

                const fieldName = entry.form.fieldMap['79'].name;
                setFormState({
                    ...formState,
                    fieldState: {
                        ...formState.fieldState,
                        [fieldName]: {
                            ...formState.fieldState[fieldName],
                            scrollTo: true,
                            invalid: true,
                        }
                    }
                });

                return;
            }
        }

        if (entry.step?.flowAdvancedOptions?.confirmations?.hasOwnProperty(status) && entry.step.flowAdvancedOptions.confirmations[status]?.show) {
            const message = entry.step.flowAdvancedOptions.confirmations[status]?.message || 'Are you sure?';

            if (!confirm(message)) {
                console.log('cancelled confirmation');
                return;
            }
        }

        let hasValidationError = false;
        // there's a bug where if a step has no editable fields and there is a require
        // administrative field on the form that isn't visible it throws a validation error
        // by adding at least 1 entry to entry.step.editableFields that
        // bug is fixed
        // NOTE: once proper tests are written around the validate function, I'll feel more
        // comfortable in fixing this
        const editableFields = [
            ...(entry.step.editableFields.length === 0 ? [''] : entry.step.editableFields),
        ];

        // perform any custom actions based on status
        if (entry.step?.flowAdvancedOptions?.customActions?.hasOwnProperty(status)) {
            const funcString = entry.step.flowAdvancedOptions.customActions[status];
            let result = true;

            // variable to hold any state updates
            let stateUpdates = {};
            try {
                // tslint:disable-next-line:no-eval
                result = eval(funcString);
            } catch (e) {
                result = true;
                console.log(e);
            }

            if (result === false) {
                console.log('cancelled per customAction');
                return
            }

            if (Object.keys(stateUpdates).length) {
                console.log('applying state updates from customAction', stateUpdates);
                setFormState((state) => ({
                    ...state,
                    fieldValues: {
                        ...state.fieldValues,
                        ...stateUpdates
                    }
                }));
            }
        }

        if (requireValidation && !validate(entry.form, formState, setFormState, editableFields)) {
            hasValidationError = true;

            // scroll to first field with validation error
            entry.form.fields.some((field: Field) => {
                const fieldName = field.name;
                if (formState.fieldState.hasOwnProperty(fieldName) && formState.fieldState[fieldName].invalid === true) {
                    setFormState({
                        ...formState,
                        fieldState: {
                            ...formState.fieldState,
                            [fieldName]: {
                                ...formState.fieldState[fieldName],
                                scrollTo: true
                            }
                        }
                    });
                    return true;
                }
                return false;
            });
        }

        // validate note mode
        if (
            (entry.step.noteMode === 'required_if_approved' && status === 'approved' && !entryNote) ||
            (entry.step.noteMode === 'required_if_rejected' && status === 'rejected' && !entryNote) ||
            (entry.step.noteMode === 'required_if_reverted' && status === 'revert' && !entryNote) ||
            (entry.step.noteMode === 'required_if_reverted_or_rejected' && (status === 'revert' || status === 'rejected') && !entryNote)
        ) {
            hasValidationError = true;
            setEntryNoteErrorMessage('A note is required');
        }

        if (hasValidationError) {
            return;
        }

        setStatusIsUpdating(status);
        Flow.getInstance().update(
            entry.form.id,
            status,
            {
                ...formState.fieldValues,
                ['__gravityflow_note']: entryNote,
                ...data
            })
            .then((response: FlowFetchResponse) => {
                if (response.ok) {
                    window.scrollTo(0, 0);

                    dispatchStatusMessage({
                        type: 'success',
                        message: `Workflow Entry #${entry.id} updated`,
                        icon: 'check-circle'
                    });

                    if (
                        !['take_ownership', 'pending', 'in_progress'].includes(status) && /* only go to Inbox when status update results in a change of step */
                        entry.step.navigateToPageAfterUpdate === 'inbox'
                    ) {
                        history.push('/inbox');
                    } else {
                        setEntryRefreshCounter(entryRefreshCounter + 1);
                    }
                } else {
                    throw Error(response?.json?.message);
                }
                setEntryNote('');
            })
            .catch((error: any) => {
                setStatusUpdateErrorMessage(((error instanceof Object && error.hasOwnProperty('message') ? error.message : error) || 'Exception updating workflow status'));
            })
            .finally(() => {
                setStatusIsUpdating('');
            })
    };

    const doAdminAction = (action: string, data: any = null): void => {
        setStatusIsUpdating('admin');
        Flow.getInstance().adminAction(entry.id, action, data)
            .then((response: FlowFetchResponse) => {
                if (response.ok) {
                    refreshEntry();
                    window.scrollTo(0, 0);
                } else {
                    setStatusUpdateErrorMessage(response.json.message || 'Error updating workflow status');
                }
                setStatusIsUpdating('');
            })
            .catch((error: any) => {
                setStatusUpdateErrorMessage(((error instanceof Object && error.hasOwnProperty('message') ? error.message : error) || 'Exception updating workflow status'));
                setStatusIsUpdating('');

            });
    }
    const doUserChangeStep = (stepId: string|number): void => {
        setStatusIsUpdating('userChangeStep');
        setUserChangeStepErrorMessage('');

        Flow.getInstance().userChangeStep(entry.id, stepId)
            .then((response: FlowFetchResponse) => {
                if (response.ok) {
                    refreshEntry();
                    window.scrollTo(0, 0);
                } else {
                    setUserChangeStepErrorMessage(response.json.message || 'Error updating workflow status');
                }
                setStatusIsUpdating('');
            })
            .catch((error: any) => {
                setUserChangeStepErrorMessage(((error instanceof Object && error.hasOwnProperty('message') ? error.message : error) || 'Exception updating workflow status'));
                setStatusIsUpdating('');
            });
    }

    const refreshEntry = () => setEntryRefreshCounter(entryRefreshCounter + 1);
    const clearEntryNoteError = () => setEntryNoteErrorMessage('');

    return {
        entry,
        formState,
        setFormState,
        refreshEntry,
        entryIsLoading,
        entryLoadErrorMessage,
        processStatusUpdate,
        isDirty: (isDirtyRef?.current === true),
        statusIsUpdating,
        statusUpdateErrorMessage,
        entryNote,
        setEntryNote,
        entryNoteErrorMessage,
        clearEntryNoteError,
        doAdminAction,
        doUserChangeStep,
        userChangeStepErrorMessage
    }
}